import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useFirestoreDoc } from './useFirestoreDoc';
import { cleanupOldDrafts } from '@/lib/draftUtils';

interface DraftData {
  content: string;
  timestamp: number;
  conversationId?: string;
  clientId?: string;
}

const DRAFT_PREFIX = 'pr-chat-draft';
const MAX_DRAFT_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days
const MAX_DRAFT_SIZE = 10000; // 10KB limit per draft
const SAVE_DEBOUNCE_MS = 1000; // 1 second debounce

export function useDraftManager(conversationId?: string, clientId?: string) {
  const { currentUser } = useAuth();
  const { data: userData } = useFirestoreDoc('users', currentUser?.uid);
  const [draftContent, setDraftContent] = useState('');
  const [lastSaveTime, setLastSaveTime] = useState<number | null>(null);
  const [saveTimeoutId, setSaveTimeoutId] = useState<NodeJS.Timeout | null>(null);

  // Check if auto-save is enabled in user settings
  const isAutoSaveEnabled = userData?.settings?.autoSave ?? true;

  // Generate unique draft key
  const getDraftKey = useCallback((convId?: string, clId?: string) => {
    if (!currentUser?.uid) return null;
    const conversation = convId || 'general';
    const client = clId || 'default';
    return `${DRAFT_PREFIX}_${currentUser.uid}_${conversation}_${client}`;
  }, [currentUser?.uid]);

  // Use the global cleanup function

  // Save draft with debouncing
  const saveDraft = useCallback((content: string) => {
    if (!isAutoSaveEnabled || typeof window === 'undefined') return;

    // Clear previous timeout
    if (saveTimeoutId) {
      clearTimeout(saveTimeoutId);
    }

    // Set new timeout for debounced save
    const timeoutId = setTimeout(() => {
      const draftKey = getDraftKey(conversationId, clientId);
      if (!draftKey) return;

      const trimmedContent = content.trim();
      
      if (trimmedContent.length === 0) {
        // Remove draft if content is empty
        localStorage.removeItem(draftKey);
        setLastSaveTime(null);
        return;
      }

      if (trimmedContent.length > MAX_DRAFT_SIZE) {
        console.warn('Draft content too large, not saving');
        return;
      }

      const draftData: DraftData = {
        content: trimmedContent,
        timestamp: Date.now(),
        conversationId,
        clientId
      };

      try {
        localStorage.setItem(draftKey, JSON.stringify(draftData));
        setLastSaveTime(Date.now());
      } catch (error) {
        console.error('Failed to save draft:', error);
      }
    }, SAVE_DEBOUNCE_MS);

    setSaveTimeoutId(timeoutId);
  }, [isAutoSaveEnabled, getDraftKey, conversationId, clientId]);

  // Load draft when conversation/client changes
  const loadDraft = useCallback(() => {
    if (typeof window === 'undefined') return '';

    const draftKey = getDraftKey(conversationId, clientId);
    if (!draftKey) return '';

    try {
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        const draftData: DraftData = JSON.parse(savedDraft);
        
        // Check if draft is not too old
        if (Date.now() - draftData.timestamp <= MAX_DRAFT_AGE) {
          setLastSaveTime(draftData.timestamp);
          return draftData.content;
        } else {
          // Remove old draft
          localStorage.removeItem(draftKey);
        }
      }
    } catch (error) {
      console.error('Failed to load draft:', error);
      // Remove corrupted draft
      const draftKey = getDraftKey(conversationId, clientId);
      if (draftKey) localStorage.removeItem(draftKey);
    }

    return '';
  }, [getDraftKey, conversationId, clientId]);

  // Clear current draft
  const clearDraft = useCallback(() => {
    if (typeof window === 'undefined') return;

    const draftKey = getDraftKey(conversationId, clientId);
    if (draftKey) {
      localStorage.removeItem(draftKey);
      setLastSaveTime(null);
    }

    // Clear any pending save timeout
    if (saveTimeoutId) {
      clearTimeout(saveTimeoutId);
      setSaveTimeoutId(null);
    }
  }, [getDraftKey, conversationId, clientId]);

  // Check if there's a draft for current context
  const hasDraft = useCallback(() => {
    if (typeof window === 'undefined') return false;

    const draftKey = getDraftKey(conversationId, clientId);
    if (!draftKey) return false;

    const savedDraft = localStorage.getItem(draftKey);
    return !!savedDraft;
  }, [getDraftKey, conversationId, clientId]);

  // Load draft when conversation or client changes
  useEffect(() => {
    if (isAutoSaveEnabled) {
      const draft = loadDraft();
      setDraftContent(draft);
      
      // Clean up old drafts periodically
      cleanupOldDrafts();
    } else {
      // Clear draft content when auto-save is disabled
      setDraftContent('');
      setLastSaveTime(null);
    }
  }, [conversationId, clientId, isAutoSaveEnabled, loadDraft]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutId) {
        clearTimeout(saveTimeoutId);
      }
    };
  }, [saveTimeoutId]);

  return {
    draftContent,
    saveDraft,
    clearDraft,
    hasDraft: hasDraft(),
    lastSaveTime,
    isAutoSaveEnabled
  };
}